import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import { AppState } from '../reducers';
import {
  loadDriverManagementDrivers,
  loadDriverManagementDriversFailure,
  loadDriverManagementDriversSuccess,
} from './driver-management-drivers.actions';
import { DriverManagementDriversApiService } from './driver-management-drivers.api.service';
import { selectDriverManagementDriversLoadedByKey } from './driver-management-drivers.selectors';

@Injectable()
export class DriverManagementDriversEffects {
  private pending: Record<string, boolean> = {};

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private api: DriverManagementDriversApiService,
  ) {}

  private toErrorMessage(error: unknown): string {
    if (!error) {
      return 'Unknown error';
    }

    if (typeof error === 'string') {
      return error;
    }

    const errorWithMessage = error as { error?: { message?: string }; message?: string };

    if (errorWithMessage.error?.message) {
      return String(errorWithMessage.error.message);
    }

    if (errorWithMessage.message) {
      return String(errorWithMessage.message);
    }

    return 'Request failed';
  }

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDriverManagementDrivers),
      mergeMap((action) =>
        this.store.select(selectDriverManagementDriversLoadedByKey(action.key)).pipe(
          take(1),
          mergeMap((isLoaded) => {
            if (isLoaded || this.pending[action.key]) {
              return EMPTY;
            }

            this.pending[action.key] = true;

            return this.api.getDriverManagementDrivers(action.request).pipe(
              map((response) => loadDriverManagementDriversSuccess({ key: action.key, response })),
              catchError((error: unknown) =>
                of(loadDriverManagementDriversFailure({ key: action.key, error: this.toErrorMessage(error) })),
              ),
              finalize(() => {
                delete this.pending[action.key];
              }),
            );
          }),
        ),
      ),
    ),
  );
}
